import { Head, router } from '@inertiajs/react'
import { buildPanoramaResponseTrackingContext, getPanoramaTrackingContext, trackPanoramaEvent, type PanoramaRespondentPath, type PanoramaStepKey } from '@/frontend/lib/panoramaTracking'
import { Check, Clock } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SurveyLayout from './components/SurveyLayout'
import ProgressBar from './components/ProgressBar'
import SectionA from './components/SectionA'
import SectionB from './components/SectionB'
import SectionC from './components/SectionC'
import SectionD from './components/SectionD'
import SectionE from './components/SectionE'
import SectionF from './components/SectionF'
import SectionG from './components/SectionG'
import SectionH from './components/SectionH'

type PageComponent = React.FC & {
  layout?: (page: React.ReactElement) => React.ReactElement
}

interface FormData {
  [key: string]: any
}

const INITIAL_FORM_DATA: FormData = {
  q1: '',
  q2: '',
  q3: '',
  q3_other: '',
  q4: [],
  q4_other: '',
  q5: '',
  q6: [],
  q6_other: '',
  q7: [],
  q7_other: '',
  q8: '',
  q9: '',
  q9_other: '',
  q10: '',
  q11: '',
  q12: '',
  q13: '',
  q14: '',
  q15: '',
  q16: '',
  q17: '',
  q17_other: '',
  q18: '',
  q19a: [],
  q19b: [],
  q20: '',
  q21: '',
  q21_email: '',
  q23: '',
  q24: '',
  q25: '',
  q26: '',
  q27: [],
  q28: [],
}

// Section indices
const SECTION_A = 0
const SECTION_B = 1
const SECTION_C = 2
const SECTION_D = 3
const SECTION_E = 4
const SECTION_F = 5
const SECTION_G = 6
const SECTION_H = 7

const SECTION_TITLES: Record<number, string> = {
  [SECTION_A]: 'Perfil da Operação',
  [SECTION_B]: 'Uso de IA Hoje',
  [SECTION_C]: 'Sobre Não Usar IA',
  [SECTION_D]: 'Governança e Processos',
  [SECTION_E]: 'Cultura e Experimentação',
  [SECTION_F]: 'Futuro e Investimento',
  [SECTION_G]: 'Encerramento',
  [SECTION_H]: 'Canais e Operações Digitais',
}

const SECTION_KEYS: Record<number, PanoramaStepKey> = {
  [SECTION_A]: 'section_a',
  [SECTION_B]: 'section_b',
  [SECTION_C]: 'section_c',
  [SECTION_D]: 'section_d',
  [SECTION_E]: 'section_e',
  [SECTION_F]: 'section_f',
  [SECTION_G]: 'section_g',
  [SECTION_H]: 'section_h',
}

function respondentPathForUsage(value: string): PanoramaRespondentPath | undefined {
  if (value === 'usamos_ativamente' || value === 'experimentando') return 'ai_user'
  if (value === 'nao_usamos' || value === 'tentamos_paramos') return 'non_ai_user'
  return undefined
}

const Index: PageComponent = () => {
  const [started, setStarted] = useState(false)
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA)
  const [currentStep, setCurrentStep] = useState(SECTION_A)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const surveyStartedAtRef = useRef<number | null>(null)
  const stepStartedAtRef = useRef<number | null>(null)
  const viewedStepCountRef = useRef(0)
  const readyStepsRef = useRef<Set<PanoramaStepKey>>(new Set())
  const videoTrackedRef = useRef(false)
  const submitInFlightRef = useRef(false)
  const lastPathTrackedRef = useRef<PanoramaRespondentPath | undefined>(undefined)

  const isAiUser = ['usamos_ativamente', 'experimentando'].includes(formData.q5)
  const respondentPath = respondentPathForUsage(formData.q5)

  // Step sequence based on respondent path.
  // Default to AI-user path (6 steps) before Q5 is answered so the
  // progress bar doesn't jump backwards when Q5 is filled in.
  const stepSequence = useMemo(() => {
    if (formData.q5 === 'nao_usamos' || formData.q5 === 'tentamos_paramos') {
      // Non-AI / stopped users: A → B → C → H → F → G (6 steps)
      return [SECTION_A, SECTION_B, SECTION_C, SECTION_H, SECTION_F, SECTION_G]
    }
    // Active AI users (and before Q5 is answered): A → B → H → D → E → F → G (7 steps)
    return [SECTION_A, SECTION_B, SECTION_H, SECTION_D, SECTION_E, SECTION_F, SECTION_G]
  }, [formData.q5])

  const currentStepIndex = stepSequence.indexOf(currentStep)
  const totalSteps = stepSequence.length
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === totalSteps - 1
  const currentStepKey = SECTION_KEYS[currentStep]
  const currentStepTitle = SECTION_TITLES[currentStep]

  const updateField = useCallback((field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setSubmitError(null)
  }, [])

  // Per-step validation
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case SECTION_A:
        return formData.q1 !== '' && formData.q2 !== '' && formData.q3 !== '' && formData.q4.length > 0
      case SECTION_B:
        if (formData.q5 === '') return false
        if (!isAiUser) return true
        return formData.q6.length > 0 && formData.q7.length > 0 && formData.q8 !== ''
      case SECTION_C:
        return formData.q9 !== '' && formData.q10 !== ''
      case SECTION_D:
        return formData.q11 !== '' && formData.q12 !== '' && formData.q13 !== '' && formData.q14 !== ''
      case SECTION_E:
        return formData.q15 !== '' && formData.q16 !== '' && formData.q17 !== ''
      case SECTION_H:
        return formData.q23 !== '' && formData.q24 !== '' && formData.q25 !== '' && formData.q26 !== ''
      case SECTION_F:
        return formData.q18 !== '' && formData.q20 !== ''
      case SECTION_G:
        if (formData.q21 === '') return false
        if (formData.q21 === 'sim' && formData.q21_email.trim() === '') return false
        return true
      default:
        return false
    }
  }, [currentStep, formData])

  const goNext = useCallback(() => {
    if (!canProceed) return
    const currentPos = stepSequence.indexOf(currentStep)
    if (currentPos < stepSequence.length - 1) {
      const nextStep = stepSequence[currentPos + 1]

      trackPanoramaEvent({
        eventName: 'panorama_step_next',
        stepKey: currentStepKey,
        stepIndex: currentStepIndex + 1,
        stepCount: totalSteps,
        respondentPath,
        metadata: {
          next_step_key: SECTION_KEYS[nextStep],
          time_on_step_ms: stepStartedAtRef.current ? Date.now() - stepStartedAtRef.current : null,
        },
      })

      setCurrentStep(nextStep)
    }
  }, [canProceed, currentStep, currentStepIndex, currentStepKey, respondentPath, stepSequence, totalSteps])

  const goBack = useCallback(() => {
    const currentPos = stepSequence.indexOf(currentStep)
    if (currentPos > 0) {
      const previousStep = stepSequence[currentPos - 1]

      trackPanoramaEvent({
        eventName: 'panorama_step_back',
        stepKey: currentStepKey,
        stepIndex: currentStepIndex + 1,
        stepCount: totalSteps,
        respondentPath,
        metadata: {
          previous_step_key: SECTION_KEYS[previousStep],
          time_on_step_ms: stepStartedAtRef.current ? Date.now() - stepStartedAtRef.current : null,
        },
      })

      setCurrentStep(previousStep)
    }
  }, [currentStep, currentStepIndex, currentStepKey, respondentPath, stepSequence, totalSteps])

  // Focus new section on step change
  useEffect(() => {
    sectionRef.current?.focus()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  useEffect(() => {
    getPanoramaTrackingContext()
    trackPanoramaEvent({
      eventName: 'panorama_landing_view',
      metadata: {
        has_referrer: Boolean(document.referrer),
      },
    })
  }, [])

  useEffect(() => {
    if (!started) return

    const now = Date.now()
    stepStartedAtRef.current = now
    viewedStepCountRef.current += 1

    trackPanoramaEvent({
      eventName: 'panorama_step_view',
      stepKey: currentStepKey,
      stepIndex: currentStepIndex + 1,
      stepCount: totalSteps,
      respondentPath,
      metadata: {
        viewed_step_count: viewedStepCountRef.current,
        survey_elapsed_ms: surveyStartedAtRef.current ? now - surveyStartedAtRef.current : null,
      },
    })
  }, [started, currentStep, currentStepIndex, currentStepKey, respondentPath, totalSteps])

  useEffect(() => {
    if (!started || !canProceed) return
    if (readyStepsRef.current.has(currentStepKey)) return

    readyStepsRef.current.add(currentStepKey)

    trackPanoramaEvent({
      eventName: 'panorama_step_ready',
      stepKey: currentStepKey,
      stepIndex: currentStepIndex + 1,
      stepCount: totalSteps,
      respondentPath,
      metadata: {
        step_title: currentStepTitle,
        time_on_step_ms: stepStartedAtRef.current ? Date.now() - stepStartedAtRef.current : null,
      },
    })
  }, [started, canProceed, currentStepIndex, currentStepKey, currentStepTitle, respondentPath, totalSteps])

  useEffect(() => {
    if (!started || !respondentPath) return
    if (lastPathTrackedRef.current === respondentPath) return

    lastPathTrackedRef.current = respondentPath

    trackPanoramaEvent({
      eventName: 'panorama_path_selected',
      stepKey: SECTION_KEYS[SECTION_B],
      stepIndex: stepSequence.indexOf(SECTION_B) + 1,
      stepCount: totalSteps,
      respondentPath,
      metadata: {
        q5: formData.q5,
      },
    })
  }, [started, respondentPath, formData.q5, stepSequence, totalSteps])

  // Warn before unload
  useEffect(() => {
    const hasData = started || formData.q1 !== '' || formData.q5 !== ''
    if (!hasData) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      if (!submitInFlightRef.current) {
        trackPanoramaEvent({
          eventName: 'panorama_abandon',
          stepKey: currentStepKey,
          stepIndex: currentStepIndex + 1,
          stepCount: totalSteps,
          respondentPath,
          metadata: {
            survey_elapsed_ms: surveyStartedAtRef.current ? Date.now() - surveyStartedAtRef.current : null,
            time_on_step_ms: stepStartedAtRef.current ? Date.now() - stepStartedAtRef.current : null,
          },
          useBeacon: true,
        })
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [started, formData.q1, formData.q5, currentStepKey, currentStepIndex, totalSteps, respondentPath])

  const handleSubmit = useCallback(() => {
    if (!canProceed || isSubmitting) return
    setIsSubmitting(true)
    setSubmitError(null)
    submitInFlightRef.current = true

    const respondentPath = isAiUser ? 'ai_user' : 'non_ai_user'

    trackPanoramaEvent({
      eventName: 'panorama_submit_attempt',
      stepKey: currentStepKey,
      stepIndex: currentStepIndex + 1,
      stepCount: totalSteps,
      respondentPath,
      metadata: {
        survey_elapsed_ms: surveyStartedAtRef.current ? Date.now() - surveyStartedAtRef.current : null,
        wants_report: formData.q21 === 'sim',
      },
    })

    const payload = {
      panorama_response: {
        revenue_range: formData.q1,
        team_size: formData.q2,
        vertical: formData.q3,
        vertical_other: formData.q3 === 'outro' ? formData.q3_other : null,
        uses_ai: isAiUser,
        respondent_path: respondentPath,
        email: formData.q21 === 'sim' ? formData.q21_email : null,
        wants_report: formData.q21 === 'sim',
        tracking_context: buildPanoramaResponseTrackingContext(),
        answers: {
          q1: formData.q1,
          q2: formData.q2,
          q3: formData.q3,
          q3_other: formData.q3_other || undefined,
          q4: formData.q4,
          q4_other: formData.q4_other || undefined,
          q5: formData.q5,
          ...(isAiUser
            ? {
                q6: formData.q6,
                q6_other: formData.q6_other || undefined,
                q7: formData.q7,
                q7_other: formData.q7_other || undefined,
                q8: formData.q8,
                q11: formData.q11,
                q12: formData.q12,
                q13: formData.q13,
                q14: formData.q14,
                q15: formData.q15,
                q16: formData.q16,
                q17: formData.q17,
                q17_other: formData.q17_other || undefined,
              }
            : {
                q9: formData.q9,
                q9_other: formData.q9_other || undefined,
                q10: formData.q10,
              }),
          q18: formData.q18,
          q19a: formData.q19a,
          q19b: formData.q19b,
          q20: formData.q20,
          q21: formData.q21,
          q23: formData.q23,
          q24: formData.q24,
          q25: formData.q25,
          q26: formData.q26,
          q27: formData.q27,
          q28: formData.q28,
        },
      },
    }

    router.post(window.location.pathname, payload, {
      onError: () => {
        setSubmitError('Erro ao enviar pesquisa. Verifique suas respostas e tente novamente.')
        setIsSubmitting(false)
        submitInFlightRef.current = false
      },
      onFinish: () => {
        setIsSubmitting(false)
        setTimeout(() => {
          const isResultPath = window.location.pathname.match(/\/panorama-ecommerce-2026\/[^/]+$/)
          if (!isResultPath) submitInFlightRef.current = false
        }, 0)
      },
    })
  }, [canProceed, isSubmitting, isAiUser, formData, currentStepKey, currentStepIndex, totalSteps])

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true)

    if (videoTrackedRef.current) return
    videoTrackedRef.current = true

    trackPanoramaEvent({
      eventName: 'panorama_video_play',
      metadata: {
        started,
      },
    })
  }, [started])

  const renderSection = () => {
    const props = { formData, updateField }

    switch (currentStep) {
      case SECTION_A:
        return <SectionA {...props} />
      case SECTION_B:
        return <SectionB {...props} />
      case SECTION_C:
        return <SectionC {...props} />
      case SECTION_D:
        return <SectionD {...props} />
      case SECTION_E:
        return <SectionE {...props} />
      case SECTION_F:
        return <SectionF {...props} />
      case SECTION_G:
        return <SectionG {...props} />
      case SECTION_H:
        return <SectionH {...props} />
      default:
        return null
    }
  }

  if (!started) {
    return (
      <>
        <Head title="Panorama E-commerce 2026" />

        <div className="py-12 md:py-20">
          <div className="flex flex-col md:grid md:grid-cols-[1fr_1fr] md:gap-10">
            {/* Header */}
            <div className="text-center md:text-left mb-6 md:mb-0 md:col-start-2 md:row-start-1">
              <p className="text-sm font-medium text-[#7c3aed] uppercase tracking-wider mb-3">
                Pesquisa 2026
              </p>
              <h1
                className="text-4xl md:text-5xl leading-[1.05] text-slate-900 dark:text-foreground mb-4 text-balance"
                style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}
              >
                Panorama de IA nos{' '}
                <span className="text-violet-400 italic">e-commerces</span>
              </h1>
              <p className="text-slate-500 dark:text-muted-foreground text-base md:text-lg">
                Descubra o nível de maturidade em IA da sua marca
                e veja como você se posiciona no mercado brasileiro.
              </p>
            </div>

            {/* Video */}
            <div className="my-6 md:my-0 md:col-start-1 md:row-start-1 md:row-span-4">
              <div className="rounded-2xl overflow-hidden shadow-lg max-w-[280px] mx-auto md:max-w-none relative group">
                <video
                  ref={videoRef}
                  className="w-full h-auto"
                  controls={isPlaying}
                  preload="none"
                  playsInline
                  aria-label="Vídeo promocional Panorama E-commerce 2026"
                  poster="https://sz-images.s3.us-west-2.amazonaws.com/landing/panorama-ecommerce-2026-poster.gif"
                  onPlay={handleVideoPlay}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                >
                  <source
                    src="https://sz-images.s3.us-west-2.amazonaws.com/landing/panorama-ecommerce-2026.mp4"
                    type="video/mp4"
                  />
                </video>
                {!isPlaying && (
                  <button
                    type="button"
                    className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors cursor-pointer"
                    onClick={() => videoRef.current?.play()}
                    aria-label="Reproduzir vídeo"
                  >
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1))',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.4)',
                        boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.4), 0 4px 20px rgba(0,0,0,0.15)',
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                        <polygon points="9 5 19 12 9 19" />
                      </svg>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Value props */}
            <div className="w-full max-w-md mx-auto md:mx-0 space-y-4 mb-8 md:mb-0 md:col-start-2 md:row-start-2">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center mt-0.5">
                  <Check size={14} className="text-[#7c3aed]" />
                </div>
                <p className="text-slate-700 dark:text-muted-foreground text-base">
                  Descubra seu score de maturidade de <span className="font-semibold">0 a 100</span> ao final da pesquisa
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center mt-0.5">
                  <Check size={14} className="text-[#7c3aed]" />
                </div>
                <p className="text-slate-700 dark:text-muted-foreground text-base">
                  Compare seu resultado com o de outras operações de e-commerce do Brasil
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center mt-0.5">
                  <Check size={14} className="text-[#7c3aed]" />
                </div>
                <p className="text-slate-700 dark:text-muted-foreground text-base">
                  Receba o relatório completo com todos os resultados por e-mail (se quiser)
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center mt-0.5">
                  <Clock size={14} className="text-[#7c3aed]" />
                </div>
                <p className="text-slate-700 dark:text-muted-foreground text-base">
                  Leva apenas <span className="font-semibold">6-8 minutos</span>
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center md:text-left md:col-start-2 md:row-start-3">
              <button
                type="button"
                onClick={() => {
                  getPanoramaTrackingContext()
                  surveyStartedAtRef.current = Date.now()
                  stepStartedAtRef.current = surveyStartedAtRef.current
                  readyStepsRef.current = new Set()
                  viewedStepCountRef.current = 0
                  lastPathTrackedRef.current = undefined
                  submitInFlightRef.current = false

                  trackPanoramaEvent({
                    eventName: 'panorama_start_click',
                    metadata: {
                      cta: 'hero_start',
                    },
                  })
                  window.fbq?.('track', 'ViewContent', { content_name: 'Panorama E-commerce 2026' })
                  setStarted(true)
                }}
                className="px-8 py-3 bg-[#7c3aed] text-white text-base font-medium rounded-xl hover:bg-[#6d28d9] transition-colors shadow-sm"
              >
                Começar pesquisa
              </button>
            </div>

            {/* Partnership note */}
            <div className="mt-8 md:mt-0 md:col-start-2 md:row-start-4">
              <p className="text-xs text-slate-400 dark:text-muted-foreground text-center md:text-left max-w-sm mx-auto md:mx-0">
                Pesquisa independente realizada por <span className="font-medium">StationZero</span> em parceria com <span className="font-medium">Synco Live</span>.
              </p>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Head title="Panorama E-commerce 2026" />

      <div className="py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-sm font-medium text-[#7c3aed] uppercase tracking-wider mb-2">
            Pesquisa 2026
          </p>
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-foreground mb-2"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}
          >
            Panorama de IA em E-commerces
          </h1>
          <p className="text-slate-500 dark:text-muted-foreground text-sm md:text-base">
            Participe e receba o relatório completo com os resultados.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <ProgressBar currentStep={currentStepIndex + 1} totalSteps={totalSteps} />
        </div>

        {/* Section Title */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-foreground">
            {SECTION_TITLES[currentStep]}
          </h2>
        </div>

        {/* Section Content */}
        <div ref={sectionRef} tabIndex={-1} className="outline-none">
          {renderSection()}
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="mt-6 bg-red-50 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 rounded-lg p-4">
            <p className="text-red-700 text-sm">{submitError}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-10 pt-6 border-t border-slate-200 dark:border-border">
          {!isFirstStep ? (
            <button
              type="button"
              onClick={goBack}
              className="px-5 py-2.5 text-sm font-medium text-slate-600 dark:text-muted-foreground hover:text-slate-900 dark:hover:text-foreground transition-colors"
            >
              ← Voltar
            </button>
          ) : (
            <div />
          )}

          {isLastStep ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canProceed || isSubmitting}
              className="px-6 py-2.5 bg-[#7c3aed] text-white text-sm font-medium rounded-lg hover:bg-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar respostas'}
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              disabled={!canProceed}
              className="px-6 py-2.5 bg-[#7c3aed] text-white text-sm font-medium rounded-lg hover:bg-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo →
            </button>
          )}
        </div>
      </div>
    </>
  )
}

Index.layout = (page: React.ReactElement) => <SurveyLayout>{page}</SurveyLayout>

export default Index
